import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Flame, Trophy, Target, Swords, Clock, CheckCircle2, Gift } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { format, differenceInDays } from 'date-fns';

interface Challenge {
  id: string;
  title: string;
  description: string | null;
  challenge_type: string;
  target_value: number;
  reward_points: number;
  week_start: string;
  week_end: string;
  is_active: boolean;
}

interface ChallengeProgress {
  challenge_id: string;
  current_value: number;
  is_completed: boolean;
}

const CHALLENGE_ICONS: Record<string, any> = {
  'play_matches': Swords,
  'win_matches': Trophy,
  'score_goals': Target,
  'assist': Gift,
  'default': Flame
};

export const WeeklyChallenges = ({ playerId }: { playerId?: string }) => {
  const { user } = useAuth();
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [progress, setProgress] = useState<Map<string, ChallengeProgress>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChallenges();
    if (playerId) fetchProgress();
  }, [playerId]);

  const fetchChallenges = async () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const { data } = await supabase
      .from('weekly_challenges')
      .select('*')
      .eq('is_active', true)
      .lte('week_start', today)
      .gte('week_end', today)
      .order('reward_points', { ascending: false });

    if (data) setChallenges(data);
    setLoading(false);
  };

  const fetchProgress = async () => {
    if (!playerId) return;
    const { data } = await supabase
      .from('player_challenge_progress')
      .select('challenge_id, current_value, is_completed')
      .eq('player_id', playerId);

    if (data) {
      const progressMap = new Map();
      data.forEach(p => progressMap.set(p.challenge_id, p));
      setProgress(progressMap);
    }
  };

  const getIcon = (type: string) => CHALLENGE_ICONS[type] || CHALLENGE_ICONS.default;

  if (loading) {
    return (
      <Card className="p-4 animate-pulse">
        <div className="h-40 bg-muted rounded" />
      </Card>
    );
  }

  return (
    <Card className="p-4 bg-gradient-to-br from-card/80 to-card border-primary/20">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-500" />
          <h3 className="font-bold text-lg">Weekly Challenges</h3>
        </div>
        {challenges[0] && (
          <Badge variant="outline" className="text-xs">
            <Clock className="w-3 h-3 mr-1" />
            {differenceInDays(new Date(challenges[0].week_end), new Date())} দিন বাকি
          </Badge>
        )}
      </div>

      {challenges.length === 0 ? (
        <div className="text-center text-muted-foreground py-8">
          <Flame className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>এই সপ্তাহে কোন চ্যালেঞ্জ নেই</p>
        </div>
      ) : (
        <div className="space-y-3">
          {challenges.map(challenge => {
            const prog = progress.get(challenge.id);
            const currentValue = prog?.current_value || 0;
            const percentage = Math.min((currentValue / challenge.target_value) * 100, 100);
            const isCompleted = prog?.is_completed || false;
            const IconComponent = getIcon(challenge.challenge_type);

            return (
              <div
                key={challenge.id}
                className={`p-3 rounded-lg border transition-all ${
                  isCompleted 
                    ? 'bg-green-500/10 border-green-500/30' 
                    : 'bg-background/50 border-border/50 hover:border-primary/30'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${isCompleted ? 'bg-green-500/20' : 'bg-primary/10'}`}>
                    {isCompleted ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <IconComponent className="w-5 h-5 text-primary" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="font-semibold text-sm">{challenge.title}</h4>
                      <Badge variant="secondary" className="shrink-0">
                        <Gift className="w-3 h-3 mr-1" />
                        {challenge.reward_points} pts
                      </Badge>
                    </div>
                    
                    {challenge.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">{challenge.description}</p>
                    )}

                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">অগ্রগতি</span>
                        <span className={isCompleted ? 'text-green-500' : 'text-primary'}>
                          {currentValue}/{challenge.target_value}
                        </span>
                      </div>
                      <Progress 
                        value={percentage} 
                        className={`h-2 ${isCompleted ? '[&>div]:bg-green-500' : ''}`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!user && (
        <p className="text-xs text-muted-foreground text-center mt-3">
          চ্যালেঞ্জে অংশ নিতে লগইন করুন
        </p>
      )}
    </Card>
  );
};
